import {
  compareCategoryCodes,
  generateNextCategoryCode,
  normalizeCategoriesWithCodes,
} from "./category-tree";
import { normalizeCategoryName } from "./utils";

export const CLONE_NAME_SUFFIX = "CÓPIA";

// Prefixo usado apenas na simulação do plano; nunca chega ao backend.
const SIMULATED_ID_PREFIX = "__category_clone__";

/**
 * Retorna a categoria e seus descendentes em ordem topológica (pai antes dos filhos).
 *
 * Irmãos são ordenados por código para que os clones herdem a mesma ordem relativa
 * exibida na árvore.
 */
export function collectCategorySubtree(categories = [], rootId) {
  const root = categories.find((category) => category.id === rootId);
  if (!root) return [];

  const childrenByParent = new Map();
  categories
    .filter((category) => category.type === root.type)
    .forEach((category) => {
      const parentId = category.parent_id || null;
      if (!parentId) return;
      if (!childrenByParent.has(parentId)) childrenByParent.set(parentId, []);
      childrenByParent.get(parentId).push(category);
    });

  const visited = new Set([rootId]);
  const ordered = [];

  const visit = (category) => {
    ordered.push(category);
    [...(childrenByParent.get(category.id) || [])]
      .sort(compareCategoryCodes)
      .forEach((child) => {
        if (visited.has(child.id)) return;
        visited.add(child.id);
        visit(child);
      });
  };

  visit(root);
  return ordered;
}

/**
 * Monta um nome de clone que não colide com os irmãos do pai de destino.
 *
 * O sufixo também mantém os nomes únicos globalmente, preservando o backfill de
 * transações antigas que resolvem a categoria por nome.
 */
export function buildCloneName(categories = [], source = {}, targetParentId = null) {
  const baseName = normalizeCategoryName(source.name);
  const taken = new Set(
    categories
      .filter((category) => (
        category.type === source.type &&
        (category.parent_id || null) === (targetParentId || null)
      ))
      .map((category) => normalizeCategoryName(category.name))
  );

  const buildName = (index) => normalizeCategoryName(
    index === 1
      ? `${baseName} (${CLONE_NAME_SUFFIX})`
      : `${baseName} (${CLONE_NAME_SUFFIX} ${index})`
  );

  let index = 1;
  while (taken.has(buildName(index))) index += 1;

  return buildName(index);
}

/**
 * Simula a clonagem da subárvore e devolve o plano de criação em ordem topológica.
 *
 * A simulação valida os códigos antes de qualquer escrita: se algum nível estourar o
 * limite de irmãos ou de profundidade, o plano é rejeitado sem criar nada.
 */
export function planCategoryClone(categories = [], rootId) {
  const normalized = normalizeCategoriesWithCodes(categories);
  const subtree = collectCategorySubtree(normalized, rootId);

  if (!subtree.length) {
    return { valid: false, message: "Categoria não encontrada.", nodes: [] };
  }

  const [root] = subtree;
  const rootParentId = normalized.some((category) => (
    category.id === root.parent_id && category.type === root.type
  )) ? root.parent_id : null;

  const working = [...normalized];
  const simulatedIdBySourceId = new Map();
  const nodes = [];

  for (const source of subtree) {
    const isRoot = source.id === root.id;
    const parentSourceId = isRoot ? null : source.parent_id || null;
    const parentId = isRoot ? rootParentId : simulatedIdBySourceId.get(parentSourceId);
    const name = buildCloneName(working, source, parentId);
    const codigo = generateNextCategoryCode(working, source.type, parentId);

    if (!codigo) {
      return {
        valid: false,
        message: "Não há código disponível para clonar esta categoria.",
        nodes: [],
      };
    }

    const simulatedId = `${SIMULATED_ID_PREFIX}${source.id}`;
    simulatedIdBySourceId.set(source.id, simulatedId);
    working.push({
      id: simulatedId,
      type: source.type,
      name,
      parent_id: parentId || null,
      codigo,
      color: source.color,
    });

    nodes.push({
      sourceId: source.id,
      parentSourceId,
      parentId: isRoot ? rootParentId : null,
      name,
      type: source.type,
      color: source.color,
      codigo,
    });
  }

  return { valid: true, nodes };
}
